import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Backdrop,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Avatar,
  Divider,
  Alert,
  AlertTitle,
  FormControlLabel,
  Switch,
  FormHelperText,
  Collapse,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Paper,
  Autocomplete,
  Snackbar,
} from '@mui/material';

import {
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Description as DescriptionIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  AttachMoney as AttachMoneyIcon,
  Link as LinkIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  Clear as ClearIcon,
  Assignment as AssignmentIcon,
  AccountBalance as AccountBalanceIcon,
  Verified as VerifiedIcon,
  Block as BlockIcon,
  Schedule as ScheduleIcon,
  Receipt as ReceiptIcon,
  ContentCopy as ContentCopyIcon,
  OpenInNew as OpenInNewIcon,
  Check as CheckIcon,
} from '@mui/icons-material';

import { useQuery, useMutation } from 'react-query';
import client from '../../../../../API';

// Status configuration with colors and icons
const statusConfig = {
  pending: { 
    icon: <ScheduleIcon fontSize="small" />, 
    label: 'Pending', 
    color: '#FFA726',
    description: 'NOA created but not yet sent'
  },
  sent: { 
    icon: <SendIcon fontSize="small" />, 
    label: 'Sent', 
    color: '#42A5F5',
    description: 'NOA sent to debtor, awaiting acknowledgment'
  },
  acknowledged: { 
    icon: <VerifiedIcon fontSize="small" />, 
    label: 'Acknowledged', 
    color: '#66BB6A',
    description: 'Debtor has acknowledged the NOA'
  },
  rejected: { 
    icon: <CancelIcon fontSize="small" />, 
    label: 'Rejected', 
    color: '#EF5350',
    description: 'NOA was rejected by debtor'
  },
  revoked: { 
    icon: <BlockIcon fontSize="small" />, 
    label: 'Revoked', 
    color: '#9E9E9E',
    description: 'NOA has been revoked'
  }
};

// Acknowledgment method configuration
const ackMethodConfig = {
  email: { icon: <EmailIcon fontSize="small" />, label: 'Email', color: '#42A5F5' },
  portal: { icon: <BusinessIcon fontSize="small" />, label: 'Portal', color: '#66BB6A' },
  ui: { icon: <DescriptionIcon fontSize="small" />, label: 'UI', color: '#FFA726' }
};

const NOADetailsView = () => {
  // Dialog state
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [openAcknowledgeDialog, setOpenAcknowledgeDialog] = useState(false);
  const [openLinkInvoiceDialog, setOpenLinkInvoiceDialog] = useState(false);
  const [selectedNOA, setSelectedNOA] = useState(null);
  const [dialogTab, setDialogTab] = useState(0);

  // Snackbar for copy feedback
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  // Create NOA form state
  const [creatorId, setCreatorId] = useState('');
  const [debtorCompanyId, setDebtorCompanyId] = useState('');
  const [noaSentTo, setNoaSentTo] = useState('');
  const [remitInstructions, setRemitInstructions] = useState({
    bank_name: '',
    account_number: '',
    routing_number: '',
    account_holder: '',
    swift_code: '',
    bank_address: '',
    beneficiary_address: ''
  });
  const [isBlanket, setIsBlanket] = useState(true);

  // Acknowledge form state
  const [ackMethod, setAckMethod] = useState('email');
  const [ackRef, setAckRef] = useState('');
  const [ackProof, setAckProof] = useState('');

  // Link invoice state
  const [invoiceId, setInvoiceId] = useState('');

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    creator: '',
    company: '',
    isBlanket: 'all',
    dateRange: 'all'
  });

  // Fetch NOAs
  const {
    data: noasData,
    isLoading: isNOAsLoading,
    refetch: refetchNOAs
  } = useQuery('listNOAs', () => client.noa.list(), {
    refetchOnWindowFocus: false
  });

  const noas = noasData || [];

  // Fetch creators for autocomplete
  const { data: creatorsData } = useQuery(
    'listCreators',
    () => client.creators.list(),
    { refetchOnWindowFocus: false }
  );

  // Fetch companies for autocomplete
  const { data: companiesResponse } = useQuery(
    'listCompanies',
    () => client.companies.list(),
    { refetchOnWindowFocus: false }
  );

  // Extract companies array from paginated response
  const companiesData = companiesResponse?.companies || [];

  // Compute selected company object for Autocomplete value
  const selectedDebtorCompany = useMemo(() =>
    companiesData?.find(company => company.id === parseInt(debtorCompanyId)) || null,
    [companiesData, debtorCompanyId]
  );

  // Create NOA mutation
  const createNOAMutation = useMutation(
    (input) => client.noa.create(input),
    {
      onSuccess: () => {
        refetchNOAs();
        resetCreateForm();
        setOpenCreateDialog(false);
        showSnackbar('NOA created successfully');
      },
      onError: (error) => {
        console.error('Error creating NOA:', error);
        alert('Failed to create NOA');
      }
    }
  );

  // Send NOA mutation
  const sendNOAMutation = useMutation(
    ({ noaId, input }) => client.noa.send(noaId, input),
    {
      onSuccess: () => {
        refetchNOAs();
        showSnackbar('NOA sent successfully! Public link generated.');
      },
      onError: (error) => {
        console.error('Error sending NOA:', error);
        alert('Failed to send NOA');
      }
    }
  );

  // Acknowledge NOA mutation
  const acknowledgeNOAMutation = useMutation(
    (input) => client.noa.acknowledge(input),
    {
      onSuccess: () => {
        refetchNOAs();
        setOpenAcknowledgeDialog(false);
        resetAcknowledgeForm();
        showSnackbar('NOA acknowledged successfully');
      },
      onError: (error) => {
        console.error('Error acknowledging NOA:', error);
        alert('Failed to acknowledge NOA');
      }
    }
  );

  // Revoke NOA mutation
  const revokeNOAMutation = useMutation(
    (noaId) => client.noa.revoke(noaId),
    {
      onSuccess: () => {
        refetchNOAs();
        showSnackbar('NOA revoked successfully');
      },
      onError: (error) => {
        console.error('Error revoking NOA:', error);
        alert('Failed to revoke NOA');
      }
    }
  );

  // Link invoice mutation
  const linkInvoiceMutation = useMutation(
    (input) => client.noa.linkInvoice(input),
    {
      onSuccess: () => {
        refetchNOAs();
        setOpenLinkInvoiceDialog(false);
        setInvoiceId('');
        showSnackbar('Invoice linked successfully');
      },
      onError: (error) => {
        console.error('Error linking invoice:', error);
        alert('Failed to link invoice');
      }
    }
  );

  const showSnackbar = (message) => {
    setSnackbar({ open: true, message });
  };

  const copyToClipboard = async (text, label = 'Link') => {
    try {
      await navigator.clipboard.writeText(text);
      showSnackbar(`${label} copied to clipboard!`);
    } catch (err) {
      console.error('Failed to copy:', err);
      showSnackbar('Failed to copy to clipboard');
    }
  };

  const resetCreateForm = () => {
    setCreatorId('');
    setDebtorCompanyId('');
    setNoaSentTo('');
    setRemitInstructions({
      bank_name: '',
      account_number: '',
      routing_number: '',
      account_holder: '',
      swift_code: '',
      bank_address: '',
      beneficiary_address: ''
    });
    setIsBlanket(true);
    setDialogTab(0);
  };

  const resetAcknowledgeForm = () => {
    setAckMethod('email');
    setAckRef('');
    setAckProof('');
  };

  const loadDefaultRemitInfo = () => {
    setRemitInstructions({
      bank_name: 'Chase Business Banking',
      account_holder: 'Blitz Systems Corp',
      account_number: '730966937',
      routing_number: '071000013',
      swift_code: 'CHASUS33',
      bank_address: '270 Park Ave., New York, NY 10017',
      beneficiary_address: '8724 Cavell Lane'
    });
  };

  const clearFilters = () => {
    setFilters({
      status: 'all',
      creator: '',
      company: '',
      isBlanket: 'all',
      dateRange: 'all'
    });
  };

  // Filter NOAs based on current filters
  const filteredNOAs = useMemo(() => {
    if (!noas) return [];
    
    return noas.filter(noa => {
      // Status filter
      if (filters.status !== 'all' && noa.status !== filters.status) return false;
      
      // Creator filter
      if (filters.creator && !noa.creator_id.toLowerCase().includes(filters.creator.toLowerCase())) return false;
      
      // Company filter
      if (filters.company && !String(noa.debtor_company_id).includes(filters.company)) return false;
      
      // Blanket filter
      if (filters.isBlanket === 'blanket' && !noa.is_blanket) return false;
      if (filters.isBlanket === 'specific' && noa.is_blanket) return false;
      
      return true;
    });
  }, [noas, filters]);

  const handleCreateNOA = () => {
    if (!creatorId || !debtorCompanyId || !noaSentTo) {
      alert('Please fill in all required fields');
      return;
    }

    const noaData = {
      creator_id: creatorId,
      debtor_company_id: parseInt(debtorCompanyId),
      noa_sent_to: noaSentTo,
      remit_instructions: remitInstructions,
      is_blanket: isBlanket
    };

    createNOAMutation.mutate(noaData);
  };

  const handleSendNOA = (noaId, invoiceDetails = null) => {
    sendNOAMutation.mutate({ noaId, input: invoiceDetails ? { invoice_details: invoiceDetails } : {} });
  };

  const handleAcknowledgeNOA = () => {
    if (!selectedNOA || !ackMethod || !ackRef) {
      alert('Please fill in all required fields');
      return;
    }

    const ackData = {
      noa_id: selectedNOA.id,
      ack_method: ackMethod,
      ack_ref: ackRef,
      ack_proof: ackProof ? { note: ackProof } : undefined
    };

    acknowledgeNOAMutation.mutate(ackData);
  };

  const handleRevokeNOA = (noaId) => {
    if (window.confirm('Are you sure you want to revoke this NOA?')) {
      revokeNOAMutation.mutate(noaId);
    }
  };

  const handleLinkInvoice = () => {
    if (!selectedNOA || !invoiceId) {
      alert('Please select an invoice');
      return;
    }

    linkInvoiceMutation.mutate({
      invoice_id: parseInt(invoiceId),
      noa_id: selectedNOA.id
    });
  };

  const coloredChipSX = (hex) => ({
    backgroundColor: `${hex}20`,
    color: hex,
    border: `1px solid ${hex}40`,
    '& .MuiChip-icon': { color: hex }
  });

  // Render enhanced header
  const renderEnhancedHeader = () => (
    <Card sx={{ mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
      <CardContent sx={{ p: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssignmentIcon sx={{ fontSize: 40 }} />
              Notice of Assignment (NOA) Management
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9 }}>
              Manage assignment notices between creators and debtor companies
            </Typography>

            <Alert
              severity="info"
              sx={{
                mt: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                '& .MuiAlert-icon': { color: 'white' }
              }}
            >
              <AlertTitle>What is an NOA?</AlertTitle>
              A Notice of Assignment formally notifies a debtor company that a creator has assigned their receivables to BlitzPay for payment processing.
            </Alert>
          </Box>

          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => setOpenCreateDialog(true)}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' }
            }}
          >
            Create NOA
          </Button>
        </Box>

        {/* Summary Stats */}
        <Box sx={{ mt: 3, p: 2, backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: 1 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            NOA Summary:
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              icon={statusConfig.pending.icon}
              label={`${noas.filter(n => n.status === 'pending').length} Pending`}
              size="small"
              variant="outlined"
              sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
            />
            <Chip
              icon={statusConfig.sent.icon}
              label={`${noas.filter(n => n.status === 'sent').length} Sent`}
              size="small"
              variant="outlined"
              sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
            />
            <Chip
              icon={statusConfig.acknowledged.icon}
              label={`${noas.filter(n => n.status === 'acknowledged').length} Acknowledged`}
              size="small"
              variant="outlined"
              sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
            />
            <Chip
              icon={statusConfig.revoked.icon}
              label={`${noas.filter(n => n.status === 'revoked').length} Revoked`}
              size="small"
              variant="outlined"
              sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
            />
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );

  // Render filter section
  const renderFilterSection = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon />
            Filters ({filteredNOAs.length} of {noas.length} NOAs)
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              disabled={JSON.stringify(filters) === JSON.stringify({
                status: 'all',
                creator: '',
                company: '',
                isBlanket: 'all',
                dateRange: 'all'
              })}
            >
              Clear
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={showFilters ? <ExpandMoreIcon /> : <FilterListIcon />}
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? 'Hide' : 'Show'} Filters
            </Button>
          </Stack>
        </Box>

        <Collapse in={showFilters}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  {Object.entries(statusConfig).map(([key, config]) => (
                    <MenuItem key={key} value={key}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {config.icon}
                        {config.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Creator ID"
                size="small"
                value={filters.creator}
                onChange={(e) => setFilters(prev => ({ ...prev, creator: e.target.value }))}
                fullWidth
                placeholder="Search by creator..."
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Company ID"
                size="small"
                value={filters.company}
                onChange={(e) => setFilters(prev => ({ ...prev, company: e.target.value }))}
                fullWidth
                placeholder="Search by company..."
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={filters.isBlanket}
                  label="Type"
                  onChange={(e) => setFilters(prev => ({ ...prev, isBlanket: e.target.value }))}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="blanket">Blanket Only</MenuItem>
                  <MenuItem value="specific">Specific Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Collapse>
      </CardContent>
    </Card>
  );

  // Render NOA card
  const renderNOACard = (noa) => {
    const statusInfo = statusConfig[noa.status] || statusConfig.pending;
    const hasPublicLink = noa.pdf_link && (noa.status === 'sent' || noa.status === 'acknowledged');
    
    return (
      <Card
        key={noa.id}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'all 0.3s ease',
          border: noa.status === 'acknowledged' ? '2px solid #66BB6A' : '1px solid #e0e0e0',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: (theme) => theme.shadows[8]
          }
        }}
      >
        <CardContent sx={{ flexGrow: 1, p: 3 }}>
          {/* Card header */}
          <Box display="flex" alignItems="center" mb={2}>
            <Avatar
              sx={{
                bgcolor: `${statusInfo.color}20`,
                color: statusInfo.color,
                mr: 2,
                width: 48,
                height: 48
              }}
            >
              {statusInfo.icon}
            </Avatar>

            <Box flexGrow={1} minWidth={0}>
              <Typography variant="h6" component="h3" noWrap sx={{ fontWeight: 600 }}>
                NOA #{noa.id}
              </Typography>
              <Chip
                icon={statusInfo.icon}
                label={statusInfo.label}
                size="small"
                variant="outlined"
                sx={coloredChipSX(statusInfo.color)}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Creator and Company Info */}
          <Stack spacing={1.5}>
            <Box display="flex" alignItems="center" gap={1}>
              <PersonIcon fontSize="small" color="action" />
              <Typography variant="body2">
                <strong>Creator:</strong> {noa.creator_id}
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={1}>
              <BusinessIcon fontSize="small" color="action" />
              <Typography variant="body2">
                <strong>Debtor Company:</strong> {noa.debtor_company_id}
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={1}>
              <EmailIcon fontSize="small" color="action" />
              <Typography variant="body2" noWrap>
                <strong>Sent To:</strong> {noa.noa_sent_to}
              </Typography>
            </Box>

            {noa.is_blanket && (
              <Chip
                label="Blanket NOA"
                size="small"
                variant="outlined"
                color="primary"
              />
            )}
          </Stack>

          {/* Public Link Section */}
          {hasPublicLink && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ 
                p: 1.5, 
                backgroundColor: '#f8f9ff', 
                borderRadius: 1,
                border: '1px solid #667eea40'
              }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#667eea', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinkIcon fontSize="small" />
                  Public NOA Link
                </Typography>
                <Box display="flex" gap={1} mt={1}>
                  <Tooltip title="Copy Link">
                    <IconButton 
                      size="small"
                      onClick={() => copyToClipboard(noa.pdf_link, 'Public link')}
                      sx={{ 
                        backgroundColor: '#667eea20',
                        '&:hover': { backgroundColor: '#667eea40' }
                      }}
                    >
                      <ContentCopyIcon fontSize="small" sx={{ color: '#667eea' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Open in New Tab">
                    <IconButton 
                      size="small"
                      onClick={() => window.open(noa.pdf_link, '_blank')}
                      sx={{ 
                        backgroundColor: '#667eea20',
                        '&:hover': { backgroundColor: '#667eea40' }
                      }}
                    >
                      <OpenInNewIcon fontSize="small" sx={{ color: '#667eea' }} />
                    </IconButton>
                  </Tooltip>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      flexGrow: 1, 
                      display: 'flex', 
                      alignItems: 'center',
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {noa.pdf_link}
                  </Typography>
                </Box>
              </Box>
            </>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Timeline */}
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Timeline:
            </Typography>
            <List dense>
              {noa.noa_sent_at && (
                <ListItem>
                  <ListItemText
                    primary="Sent"
                    secondary={new Date(noa.noa_sent_at).toLocaleString()}
                  />
                </ListItem>
              )}
              {noa.noa_ack_at && (
                <ListItem>
                  <ListItemText
                    primary={`Acknowledged via ${noa.noa_ack_method}`}
                    secondary={new Date(noa.noa_ack_at).toLocaleString()}
                  />
                </ListItem>
              )}
              {noa.revoked_at && (
                <ListItem>
                  <ListItemText
                    primary="Revoked"
                    secondary={new Date(noa.revoked_at).toLocaleString()}
                  />
                </ListItem>
              )}
            </List>
          </Box>
        </CardContent>

        {/* Footer Actions */}
        <Box sx={{ p: 2, pt: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Box display="flex" gap={1}>
            <Tooltip title="View Details">
              <IconButton 
                size="small"
                onClick={() => {
                  setSelectedNOA(noa);
                  setOpenDetailsDialog(true);
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {noa.status === 'pending' && (
              <Tooltip title="Send NOA">
                <IconButton 
                  size="small"
                  color="primary"
                  onClick={() => handleSendNOA(noa.id)}
                >
                  <SendIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {noa.status === 'sent' && (
              <Tooltip title="Acknowledge">
                <IconButton 
                  size="small"
                  color="success"
                  onClick={() => {
                    setSelectedNOA(noa);
                    setOpenAcknowledgeDialog(true);
                  }}
                >
                  <CheckCircleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {(noa.status === 'sent' || noa.status === 'acknowledged') && (
              <>
                <Tooltip title="Link Invoice">
                  <IconButton 
                    size="small"
                    color="info"
                    onClick={() => {
                      setSelectedNOA(noa);
                      setOpenLinkInvoiceDialog(true);
                    }}
                  >
                    <LinkIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Revoke NOA">
                  <IconButton 
                    size="small"
                    color="error"
                    onClick={() => handleRevokeNOA(noa.id)}
                  >
                    <BlockIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>

          <Typography variant="caption" color="text.secondary">
            Created: {new Date(noa.created_at).toLocaleDateString()}
          </Typography>
        </Box>
      </Card>
    );
  };

  return (
    <Box sx={{ padding: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Loading */}
      <Backdrop
        open={isNOAsLoading}
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        action={
          <IconButton
            size="small"
            color="inherit"
            onClick={() => setSnackbar({ ...snackbar, open: false })}
          >
            <CheckIcon fontSize="small" />
          </IconButton>
        }
      />

      {/* Header */}
      {renderEnhancedHeader()}

      {/* Filters */}
      {renderFilterSection()}

      {/* Grid of NOAs */}
      <Grid container spacing={3}>
        {filteredNOAs.map((noa) => (
          <Grid item xs={12} sm={6} lg={4} key={noa.id}>
            {renderNOACard(noa)}
          </Grid>
        ))}

        {filteredNOAs.length === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <AssignmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No NOAs Found
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {noas.length === 0 
                  ? 'Create your first Notice of Assignment to get started.'
                  : 'Try adjusting your filters to see more results.'}
              </Typography>
              {noas.length === 0 && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenCreateDialog(true)}
                >
                  Create First NOA
                </Button>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Create NOA Dialog */}
      <Dialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold' }}>
            Create New NOA
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create a Notice of Assignment for a creator-debtor relationship
          </Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ px: 3, py: 2 }}>
          <Tabs value={dialogTab} onChange={(e, v) => setDialogTab(v)} sx={{ mb: 3 }}>
            <Tab label="Basic Information" />
            <Tab label="Remit Instructions" />
          </Tabs>

          {dialogTab === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Autocomplete
                  options={creatorsData || []}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    if (option.display_name) return option.display_name;
                    if (option.creator) return option.creator;
                    return option.id ? String(option.id) : '';
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Creator *"
                      placeholder="Select or enter creator ID"
                      required
                    />
                  )}
                  freeSolo
                  value={creatorId}
                  onChange={(event, newValue) => {
                    setCreatorId(typeof newValue === 'string' ? newValue : newValue?.creator || newValue?.display_name || '');
                  }}
                  onInputChange={(event, newInputValue) => {
                    setCreatorId(newInputValue);
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  options={companiesData || []}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option.id ? `${option.name} (ID: ${option.id})` : '';
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Debtor Company *"
                      placeholder="Select or search for debtor company"
                      required
                    />
                  )}
                  value={selectedDebtorCompany}
                  onChange={(event, newValue) => setDebtorCompanyId(newValue?.id || '')}
                  filterOptions={(options, { inputValue }) => {
                    if (!inputValue) return options;
                    const searchTerm = inputValue.toLowerCase();
                    return options.filter(option => 
                      option.name?.toLowerCase().includes(searchTerm) || 
                      String(option.id).includes(searchTerm)
                    );
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="NOA Sent To (AP Email) *"
                  fullWidth
                  type="email"
                  value={noaSentTo}
                  onChange={(e) => setNoaSentTo(e.target.value)}
                  placeholder="ap@company.com"
                  required
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active' }} />
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isBlanket}
                      onChange={(e) => setIsBlanket(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Blanket NOA (covers all future invoices)"
                />
                <FormHelperText>
                  Blanket NOAs apply to all current and future receivables. Uncheck for invoice-specific NOAs.
                </FormHelperText>
              </Grid>
            </Grid>
          )}

          {dialogTab === 1 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <AlertTitle>Payment Instructions</AlertTitle>
                  These details will be shared with the debtor company for payment processing.
                </Alert>
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={loadDefaultRemitInfo}
                  startIcon={<AccountBalanceIcon />}
                  sx={{ mb: 1 }}
                >
                  Use Default Remit To (Blitz Systems Corp)
                </Button>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Bank Name"
                  fullWidth
                  value={remitInstructions.bank_name}
                  onChange={(e) => setRemitInstructions(prev => ({ ...prev, bank_name: e.target.value }))}
                  InputProps={{
                    startAdornment: <AccountBalanceIcon sx={{ mr: 1, color: 'action.active' }} />
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Account Holder Name"
                  fullWidth
                  value={remitInstructions.account_holder}
                  onChange={(e) => setRemitInstructions(prev => ({ ...prev, account_holder: e.target.value }))}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Account Number"
                  fullWidth
                  value={remitInstructions.account_number}
                  onChange={(e) => setRemitInstructions(prev => ({ ...prev, account_number: e.target.value }))}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Routing Number"
                  fullWidth
                  value={remitInstructions.routing_number}
                  onChange={(e) => setRemitInstructions(prev => ({ ...prev, routing_number: e.target.value }))}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="SWIFT Code"
                  fullWidth
                  value={remitInstructions.swift_code}
                  onChange={(e) => setRemitInstructions(prev => ({ ...prev, swift_code: e.target.value }))}
                  placeholder="Optional for international transfers"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Bank Address"
                  fullWidth
                  value={remitInstructions.bank_address}
                  onChange={(e) => setRemitInstructions(prev => ({ ...prev, bank_address: e.target.value }))}
                  placeholder="Full bank address"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Beneficiary Address"
                  fullWidth
                  value={remitInstructions.beneficiary_address}
                  onChange={(e) => setRemitInstructions(prev => ({ ...prev, beneficiary_address: e.target.value }))}
                  placeholder="Account holder's address"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenCreateDialog(false)} size="large">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateNOA}
            size="large"
            disabled={createNOAMutation.isLoading}
            startIcon={createNOAMutation.isLoading ? <CircularProgress size={16} /> : <AddIcon />}
          >
            {createNOAMutation.isLoading ? 'Creating...' : 'Create NOA'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog
        open={openDetailsDialog}
        onClose={() => setOpenDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              NOA Details - #{selectedNOA?.id}
            </Typography>
            {selectedNOA?.pdf_link && (selectedNOA?.status === 'sent' || selectedNOA?.status === 'acknowledged') && (
              <Stack direction="row" spacing={1}>
                <Tooltip title="Copy Public Link">
                  <IconButton 
                    size="small"
                    onClick={() => copyToClipboard(selectedNOA.pdf_link, 'Public link')}
                    color="primary"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Open Public Link">
                  <IconButton 
                    size="small"
                    onClick={() => window.open(selectedNOA.pdf_link, '_blank')}
                    color="primary"
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedNOA && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Assignment Information
                    </Typography>
                    <Stack spacing={1}>
                      <Typography variant="body2">
                        <strong>Creator:</strong> {selectedNOA.creator_id}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Debtor Company:</strong> {selectedNOA.debtor_company_id}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Status:</strong> {statusConfig[selectedNOA.status]?.label}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Type:</strong> {selectedNOA.is_blanket ? 'Blanket' : 'Specific'}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Public Link Card */}
              {selectedNOA.pdf_link && (selectedNOA.status === 'sent' || selectedNOA.status === 'acknowledged') && (
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ backgroundColor: '#f8f9ff', borderColor: '#667eea' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#667eea' }}>
                        <LinkIcon />
                        Public NOA Link
                      </Typography>
                      <Stack spacing={1}>
                        <TextField
                          fullWidth
                          size="small"
                          value={selectedNOA.pdf_link}
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <Tooltip title="Copy">
                                <IconButton 
                                  size="small"
                                  onClick={() => copyToClipboard(selectedNOA.pdf_link, 'Public link')}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )
                          }}
                        />
                        <Button
                          variant="outlined"
                          fullWidth
                          startIcon={<OpenInNewIcon />}
                          onClick={() => window.open(selectedNOA.pdf_link, '_blank')}
                        >
                          Open Public NOA Page
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {selectedNOA.remit_instructions && Object.keys(selectedNOA.remit_instructions).length > 0 && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Remit Instructions
                      </Typography>
                      <Stack spacing={1}>
                        {Object.entries(selectedNOA.remit_instructions).map(([key, value]) => (
                          value && <Typography key={key} variant="body2">
                            <strong>{key.replace(/_/g, ' ').toUpperCase()}:</strong> {value}
                          </Typography>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {selectedNOA.noa_ack_method && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Acknowledgment Details
                      </Typography>
                      <Stack spacing={1}>
                        <Typography variant="body2">
                          <strong>Method:</strong> {selectedNOA.noa_ack_method}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Reference:</strong> {selectedNOA.noa_ack_ref}
                        </Typography>
                        {selectedNOA.noa_ack_at && (
                          <Typography variant="body2">
                            <strong>Date:</strong> {new Date(selectedNOA.noa_ack_at).toLocaleString()}
                          </Typography>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Acknowledge Dialog */}
      <Dialog
        open={openAcknowledgeDialog}
        onClose={() => setOpenAcknowledgeDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Acknowledge NOA #{selectedNOA?.id}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Acknowledgment Method *</InputLabel>
              <Select
                value={ackMethod}
                label="Acknowledgment Method *"
                onChange={(e) => setAckMethod(e.target.value)}
              >
                {Object.entries(ackMethodConfig).map(([key, config]) => (
                  <MenuItem key={key} value={key}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {config.icon}
                      {config.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Acknowledgment Reference *"
              fullWidth
              value={ackRef}
              onChange={(e) => setAckRef(e.target.value)}
              placeholder="e.g., email Message-ID or portal reference"
              required
            />

            <TextField
              label="Additional Proof/Notes"
              fullWidth
              multiline
              rows={3}
              value={ackProof}
              onChange={(e) => setAckProof(e.target.value)}
              placeholder="Add any additional notes or proof details..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAcknowledgeDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAcknowledgeNOA}
            disabled={acknowledgeNOAMutation.isLoading}
            startIcon={acknowledgeNOAMutation.isLoading ? <CircularProgress size={16} /> : <CheckCircleIcon />}
          >
            {acknowledgeNOAMutation.isLoading ? 'Acknowledging...' : 'Acknowledge'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Link Invoice Dialog */}
      <Dialog
        open={openLinkInvoiceDialog}
        onClose={() => setOpenLinkInvoiceDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Link Invoice to NOA #{selectedNOA?.id}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Invoice ID *"
            fullWidth
            type="number"
            value={invoiceId}
            onChange={(e) => setInvoiceId(e.target.value)}
            placeholder="Enter invoice ID"
            required
            InputProps={{
              startAdornment: <ReceiptIcon sx={{ mr: 1, color: 'action.active' }} />
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLinkInvoiceDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleLinkInvoice}
            disabled={linkInvoiceMutation.isLoading}
            startIcon={linkInvoiceMutation.isLoading ? <CircularProgress size={16} /> : <LinkIcon />}
          >
            {linkInvoiceMutation.isLoading ? 'Linking...' : 'Link Invoice'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NOADetailsView;